'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { resolveOverlayTimeline, timelineKeyboardIndex, timelinePositionIndex } from '../_pure/overlayTimeline.js';
export default function TimelineOverlay({ model, onIntent }) {
    const timeline = resolveOverlayTimeline(model);
    if (!timeline)
        return null;
    return _jsx(ActiveTimelineOverlay, { timeline: timeline, onIntent: onIntent });
}
function ActiveTimelineOverlay({ timeline, onIntent }) {
    const { label, positions, controls, selectedIndex } = timeline;
    const count = positions.options.length;
    const enabled = positions.enabled ?? true;
    const progress = count === 1 ? 0 : selectedIndex / (count - 1) * 100;
    const trackRef = useRef(null);
    const isDragging = useRef(false);
    const [hovering, setHovering] = useState(false);
    // Keep stable refs so the window listeners always call the latest logic
    const selectRef = useRef(() => { });
    selectRef.current = (index) => {
        const option = positions.options[index];
        if (enabled && option && index !== selectedIndex) {
            onIntent({ overlayIntentKind: 'select-changed', sectionId: positions.sectionId, value: option.value });
        }
    };
    const indexFromX = (clientX) => {
        const el = trackRef.current;
        if (!el)
            return undefined;
        const bounds = el.getBoundingClientRect();
        return bounds.width > 0 ? timelinePositionIndex((clientX - bounds.left) / bounds.width, count) : undefined;
    };
    const indexFromXRef = useRef(indexFromX);
    indexFromXRef.current = indexFromX;
    useEffect(() => {
        const onMove = (e) => {
            if (!isDragging.current)
                return;
            const index = indexFromXRef.current(e.clientX);
            if (index !== undefined)
                selectRef.current(index);
        };
        const onUp = () => { isDragging.current = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);
    const toggleAction = controls.actions.find(a => a.playbackRole === 'toggle');
    const isPlaying = toggleAction?.isActive ?? false;
    const buttonBase = {
        padding: '5px 11px', borderRadius: 7, fontSize: 13,
        cursor: 'pointer', transition: 'background 0.12s, opacity 0.12s',
        display: 'flex', alignItems: 'center', gap: 5,
    };
    const ghostButton = {
        ...buttonBase,
        border: '1px solid rgba(255,255,255,0.18)',
        background: 'transparent', color: 'rgba(255,255,255,0.82)',
    };
    return (_jsxs("section", { "aria-label": label, style: {
            position: 'absolute', bottom: 12, left: 12, right: 12, maxWidth: 960, margin: '0 auto',
            borderRadius: 14, border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(6,9,18,0.55)', backdropFilter: 'blur(18px)',
            color: '#fff', pointerEvents: 'auto', overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
        }, children: [_jsx("div", { style: { height: 2, background: 'linear-gradient(90deg,#6366f1,#8b5cf6 55%,#06b6d4)' } }), _jsxs("div", { style: { padding: '10px 16px 13px' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }, children: [_jsx("span", { title: positions.options[selectedIndex]?.label, style: {
                                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.92)',
                                }, children: positions.options[selectedIndex]?.label }), _jsxs("span", { style: {
                                    flexShrink: 0, fontSize: 11, fontVariantNumeric: 'tabular-nums',
                                    color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.08)',
                                    padding: '2px 8px', borderRadius: 20,
                                }, children: [selectedIndex + 1, " / ", count] })] }), _jsxs("div", { ref: trackRef, role: "slider", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": count - 1, "aria-valuenow": selectedIndex, "aria-valuetext": positions.options[selectedIndex]?.label, "aria-disabled": !enabled, tabIndex: enabled ? 0 : -1, onMouseEnter: () => setHovering(true), onMouseLeave: () => setHovering(false), onMouseDown: e => {
                            if (!enabled)
                                return;
                            isDragging.current = true;
                            const index = indexFromXRef.current(e.clientX);
                            if (index !== undefined)
                                selectRef.current(index);
                        }, onTouchStart: e => {
                            if (!enabled)
                                return;
                            const t = e.touches[0];
                            if (t) {
                                const i = indexFromXRef.current(t.clientX);
                                if (i !== undefined)
                                    selectRef.current(i);
                            }
                        }, onTouchMove: e => {
                            if (!enabled)
                                return;
                            const t = e.touches[0];
                            if (t) {
                                const i = indexFromXRef.current(t.clientX);
                                if (i !== undefined)
                                    selectRef.current(i);
                            }
                        }, onKeyDown: e => {
                            const index = timelineKeyboardIndex(e.key, selectedIndex, count);
                            if (index !== undefined) {
                                e.preventDefault();
                                selectRef.current(index);
                            }
                        }, style: {
                            position: 'relative', height: 32, margin: '0 2px 10px',
                            cursor: enabled ? (isDragging.current ? 'grabbing' : 'grab') : 'default',
                            opacity: enabled ? 1 : 0.4, userSelect: 'none',
                        }, children: [_jsx("span", { style: {
                                    position: 'absolute', left: 0, right: 0,
                                    top: hovering ? 13 : 15, height: hovering ? 6 : 2,
                                    borderRadius: 4, background: 'rgba(255,255,255,0.15)',
                                    pointerEvents: 'none', transition: 'top 0.15s, height 0.15s',
                                } }), _jsx("span", { style: {
                                    position: 'absolute', left: 0, width: `${progress}%`,
                                    top: hovering ? 13 : 15, height: hovering ? 6 : 2,
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg,#6366f1,#8b5cf6)',
                                    pointerEvents: 'none', transition: 'top 0.15s, height 0.15s',
                                } }), positions.options.map((option, index) => {
                                const isSel = index === selectedIndex;
                                const isPast = index < selectedIndex;
                                return (_jsx("span", { title: option.label, style: {
                                        position: 'absolute',
                                        left: `${count === 1 ? 50 : index / (count - 1) * 100}%`,
                                        top: isSel ? 7 : 11,
                                        width: isSel ? 18 : 8, height: isSel ? 18 : 8,
                                        borderRadius: '50%', transform: 'translateX(-50%)',
                                        background: isSel ? '#fff' : isPast ? '#8b5cf6' : 'rgba(255,255,255,0.35)',
                                        border: isSel ? '2.5px solid #6366f1' : 'none',
                                        boxShadow: isSel ? '0 0 10px rgba(99,102,241,0.9)' : 'none',
                                        pointerEvents: 'none', transition: 'all 0.15s',
                                    } }, option.value));
                            })] }), _jsxs("div", { role: "group", "aria-label": "Playback controls", style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [controls.actions.map(action => {
                                const isToggle = action.playbackRole === 'toggle';
                                const isReset = action.playbackRole === 'reset';
                                const isPrev = action.label === 'Prev';
                                const isNext = action.label === 'Next';
                                const disabled = action.enabled === false;
                                if (isToggle)
                                    return (_jsxs("button", { type: "button", "aria-pressed": action.isActive ?? false, disabled: disabled, onClick: () => onIntent({ overlayIntentKind: 'action-triggered', actionId: action.actionId }), style: {
                                            ...buttonBase,
                                            border: 'none',
                                            background: isPlaying ? 'rgba(99,102,241,0.38)' : '#6366f1',
                                            color: '#fff', fontWeight: 600,
                                            opacity: disabled ? 0.38 : 1,
                                            cursor: disabled ? 'default' : 'pointer',
                                            padding: '5px 16px',
                                        }, children: [isPlaying ? '⏸' : '▶', "\u00A0", action.label] }, action.actionId));
                                if (isReset)
                                    return (_jsxs(React.Fragment, { children: [_jsx("div", { style: { width: 1, height: 18, background: 'rgba(255,255,255,0.14)', margin: '0 1px' } }), _jsxs("button", { type: "button", disabled: disabled, onClick: () => onIntent({ overlayIntentKind: 'action-triggered', actionId: action.actionId }), style: { ...ghostButton, opacity: disabled ? 0.38 : 1, cursor: disabled ? 'default' : 'pointer' }, children: ["\u21BA\u00A0", action.label] })] }, action.actionId));
                                return (_jsxs(React.Fragment, { children: [isPrev && (_jsx("button", { type: "button", "aria-label": "Rewind to start", title: "Rewind to start", disabled: !enabled || selectedIndex === 0, onClick: () => selectRef.current(0), style: { ...ghostButton, padding: '5px 9px', fontSize: 15,
                                                opacity: (!enabled || selectedIndex === 0) ? 0.3 : 1,
                                                cursor: (!enabled || selectedIndex === 0) ? 'default' : 'pointer' }, children: "\u23EE" })), _jsxs("button", { type: "button", disabled: disabled, onClick: () => onIntent({ overlayIntentKind: 'action-triggered', actionId: action.actionId }), style: { ...ghostButton, opacity: disabled ? 0.38 : 1, cursor: disabled ? 'default' : 'pointer' }, children: [isPrev && '◀ ', action.label, isNext && ' ▶'] })] }, action.actionId));
                            }), _jsx("button", { type: "button", "aria-label": "Fast-forward 3 steps", title: "Fast-forward 3 steps", disabled: !enabled || selectedIndex === count - 1, onClick: () => selectRef.current(Math.min(count - 1, selectedIndex + 3)), style: { ...ghostButton, padding: '5px 9px', fontSize: 15,
                                    opacity: (!enabled || selectedIndex === count - 1) ? 0.3 : 1,
                                    cursor: (!enabled || selectedIndex === count - 1) ? 'default' : 'pointer' }, children: "\u23ED" }), controls.description && (_jsx("span", { style: {
                                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    fontSize: 12, color: 'rgba(255,255,255,0.42)', marginLeft: 4,
                                }, children: controls.description }))] })] })] }));
}
